import { useForm, Resolver } from 'react-hook-form';
import {
  Text,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Switch,
  useToast,
  InputGroup,
  Box,
} from '@chakra-ui/react';
import { modalFormSchema, type ModalFormData } from './schemas';
import { v4 as uuidv4 } from 'uuid';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField } from '@/components/InputField';
import { PredictionsStatus } from '@/model/predictions';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { usePostPredictionsMutation } from '@/services/queries/predictions';
import { useRef } from 'react';
import { CircleFileUpload } from '@/components/CircleFileUpload';
import { ImagePreview } from '@/components/ImagePreview';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { formatDateToDateTimeLocalInputValue } from '@/utils/formatDateToDateTimeLocalInputValue';
import { useTranslation } from 'react-i18next';
import ControlTeamFormSelect from '@/components/ControlTeamFormSelect';
import { compressImage } from '@/utils/image';

export interface PredictionsCreateModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  onDone: VoidFunction;
}

export const PredictionsCreateModal = ({ isOpen, onClose, onDone }: PredictionsCreateModalProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const postMutation = usePostPredictionsMutation();

  const formRef = useRef<HTMLFormElement>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const resolver: Resolver<ModalFormData> = zodResolver(modalFormSchema);

  const form = useForm<ModalFormData>({
    resolver,
    defaultValues: {
      identifier: '',
      title: '',
      description: '',
      status: PredictionsStatus.PUBLISHED,
      disable_comments: false,
      enable_home: true,
      has_closing_date: false,
      closing_date: '',
    },
  });

  const {
    register,
    formState: { errors },
    watch,
    setValue,
    reset,
    handleSubmit,
    control,
  } = form;
  const formValues = watch();

  const renameFile = (file: File, newName: string): File => {
    return new File([file], newName, {
      type: file.type,
      lastModified: file.lastModified,
    });
  };

  const handlePreviewActionClick = (index: number) => {
    const indexToLetter = {
      0: 'a',
      1: 'b',
    } as const;

    const letter = indexToLetter[index as 0 | 1];

    setValue(`option_${letter}_image`, undefined);

    setTimeout(() => {
      inputRefs.current[index]?.click();
    }, 100);
  };

  const handlePreviewDeleteClick = (index: number) => {
    const indexToLetter = {
      0: 'a',
      1: 'b',
    } as const;

    const letter = indexToLetter[index as 0 | 1];

    if (inputRefs?.current?.[index]) {
      const input = inputRefs.current[index] as HTMLInputElement;
      input.value = '';
      input.files = null;
    }

    setValue(`option_${letter}_image`, undefined);

    setTimeout(() => {
      inputRefs.current[index]?.click();
    }, 100);
  };

  const handleCreate = (data: ModalFormData) => {
    const addOption = (file: File) => {
      const uuidOption1 = uuidv4();
      const extension = file.name.split('.').pop();
      const renamed = renameFile(file, `${uuidOption1}.${extension}`);
      return {
        file: renamed,
        uuid: uuidOption1,
      };
    };

    const { file: fileOptionA, uuid: uuidOptionA } = addOption(data.option_a_image);
    const { file: fileOptionB, uuid: uuidOptionB } = addOption(data.option_b_image);

    const mediaFiles: Record<string, File> = {};
    mediaFiles[uuidOptionA] = fileOptionA;
    mediaFiles[uuidOptionB] = fileOptionB;

    const predictionsOptions: { uuid: string; option_name: string }[] = [];
    predictionsOptions.push({ uuid: uuidOptionA, option_name: data.option_a_name });
    predictionsOptions.push({ uuid: uuidOptionB, option_name: data.option_b_name });

    const payload = {
      ...(data.team_uuid ? { team_uuid: data.team_uuid } : {}),
      identifier: data.identifier,
      title: data.title,
      description: data.description,
      closing_date: data.has_closing_date ? data.closing_date : undefined,
      disable_comments: data.disable_comments,
      enable_home: data.enable_home,
      status: data.status,
      predictions_options: predictionsOptions,
      ...mediaFiles,
    };

    postMutation.mutate(payload, {
      onSuccess: () => {
        toast({
          title: t('pages.application_predictions.components.predictions_create_modal.predictions_create_success'),
          status: 'success',
        });

        onDone?.();
        reset();
      },

      onError: (e: any) => {
        toast({
          title: e?.message || t('pages.application_predictions.components.predictions_create_modal.predictions_create_error'),
          status: 'error',
        });
      },
    });
  };

  const handleClose = () => {
    onClose();
    reset();
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose}>
        <ModalOverlay />
        <ModalContent as='form' ref={formRef} onSubmit={handleSubmit(handleCreate)}>
          <ModalHeader display='flex' justifyContent='space-between' alignItems='center' gap={2}>
            {t('pages.application_predictions.components.predictions_create_modal.predictions_create_title')}
          </ModalHeader>
          <ModalBody>
            <Flex direction='column' gap={4}>
              <ControlTeamFormSelect<ModalFormData, 'team_uuid'>
                control={control}
                name='team_uuid'
                prefetchOnOpen
                label={t('pages.application_predictions.components.predictions_create_modal.predictions_create_team_label')}
                placeholder={t('pages.application_predictions.components.predictions_create_modal.predictions_create_team_label')}
                showFlag
                width='100%'
                usePortal={false}
                error={errors?.team_uuid?.message as string | undefined}
              />

              <InputField
                withoutFloating
                label={t('pages.application_predictions.components.predictions_create_modal.predictions_create_identifier_label')}
                helperText={t(
                  'pages.application_predictions.components.predictions_create_modal.predictions_create_identifier_helper_text',
                )}
                maxLength={20}
                {...register('identifier')}
                rootProps={{ isInvalid: !!errors?.identifier, errorMessage: errors?.identifier?.message }}
              />

              <InputField
                withoutFloating
                label={t('pages.application_predictions.components.predictions_create_modal.predictions_create_title_label')}
                helperText={t(
                  'pages.application_predictions.components.predictions_create_modal.predictions_create_title_helper_text',
                )}
                maxLength={50}
                {...register('title')}
                rootProps={{ isInvalid: !!errors?.title, errorMessage: errors?.title?.message }}
              />

              <InputField
                withoutFloating
                label={t(
                  'pages.application_predictions.components.predictions_create_modal.predictions_create_description_label',
                )}
                helperText={t(
                  'pages.application_predictions.components.predictions_create_modal.predictions_create_description_helper_text',
                )}
                maxLength={100}
                {...register('description')}
                rootProps={{ isInvalid: !!errors?.description, errorMessage: errors?.description?.message }}
              />

              <InputGroup display='flex' flexDirection='column' gap={3}>
                <Text fontWeight='medium'>
                  {t('pages.application_predictions.components.predictions_create_modal.predictions_create_prediction_label')}
                </Text>

                <Box>
                  <Flex gap={3} alignItems='center'>
                    {!formValues.option_a_image ? (
                      <Flex minW='80px' w='80px' minH='80px' h='80px' justifyContent='center' alignItems='center'>
                        <CircleFileUpload
                          ref={el => (inputRefs.current[0] = el)}
                          label={t(
                            'pages.application_predictions.components.predictions_create_modal.predictions_create_image_label',
                          )}
                          onFileChange={async file => {
                            if (!file) return;

                            const compressedFile = await compressImage(file);
                            form.setValue('option_a_image', compressedFile);
                          }}
                        />
                      </Flex>
                    ) : (
                      <ImagePreview
                        src={formValues?.option_a_image ? URL.createObjectURL(formValues?.option_a_image) : undefined}
                        actionIconName='image'
                        onActionClick={() => handlePreviewActionClick(0)}
                        onDeleteClick={() => handlePreviewDeleteClick(0)}
                      />
                    )}

                    <InputField
                      withoutFloating
                      label={t(
                        'pages.application_predictions.components.predictions_create_modal.predictions_create_option_1_label',
                      )}
                      helperText={t(
                        'pages.application_predictions.components.predictions_create_modal.predictions_create_option_1_helper_text',
                      )}
                      maxLength={50}
                      width='100%'
                      {...form.register('option_a_name')}
                      rootProps={{
                        isInvalid: !!errors?.option_a_name,
                        errorMessage: errors?.option_a_name?.message?.toString(),
                      }}
                    />
                  </Flex>

                  {form.formState.errors?.option_a_image ? (
                    <Text fontSize='14px' color='red.500'>
                      {form.formState.errors?.option_a_image?.message?.toString()}
                    </Text>
                  ) : null}
                </Box>

                <Box>
                  <Flex gap={3} alignItems='center'>
                    {!formValues.option_b_image ? (
                      <Flex minW='80px' w='80px' minH='80px' h='80px' justifyContent='center' alignItems='center'>
                        <CircleFileUpload
                          ref={el => (inputRefs.current[1] = el)}
                          label={t(
                            'pages.application_predictions.components.predictions_create_modal.predictions_create_image_label',
                          )}
                          onFileChange={async file => {
                            if (!file) return;

                            const compressedFile = await compressImage(file);
                            form.setValue('option_b_image', compressedFile);
                          }}
                        />
                      </Flex>
                    ) : (
                      <ImagePreview
                        src={formValues?.option_b_image ? URL.createObjectURL(formValues?.option_b_image) : undefined}
                        actionIconName='image'
                        onActionClick={() => handlePreviewActionClick(1)}
                        onDeleteClick={() => handlePreviewDeleteClick(1)}
                      />
                    )}

                    <InputField
                      withoutFloating
                      label={t(
                        'pages.application_predictions.components.predictions_create_modal.predictions_create_option_2_label',
                      )}
                      helperText={t(
                        'pages.application_predictions.components.predictions_create_modal.predictions_create_option_2_helper_text',
                      )}
                      maxLength={50}
                      width='100%'
                      {...form.register('option_b_name')}
                      rootProps={{
                        isInvalid: !!errors?.option_b_name,
                        errorMessage: errors?.option_b_name?.message?.toString(),
                      }}
                    />
                  </Flex>

                  {form.formState.errors?.option_b_image ? (
                    <Text fontSize='14px' color='red.500'>
                      {form.formState.errors?.option_b_image?.message?.toString()}
                    </Text>
                  ) : null}
                </Box>
              </InputGroup>

              <Switch
                isChecked={!!formValues.has_closing_date}
                onChange={event => setValue('has_closing_date', event.target.checked)}
                fontWeight='normal'
                display='flex'
                alignItems='center'
              >
                {t('pages.application_predictions.components.predictions_create_modal.predictions_create_set_closing_date')}
              </Switch>

              {formValues?.has_closing_date ? (
                <InputField
                  withoutFloating
                  label={t(
                    'pages.application_predictions.components.predictions_create_modal.predictions_create_closing_date_label',
                  )}
                  type='datetime-local'
                  fullDatePickerArea
                  min={formatDateToDateTimeLocalInputValue(new Date())}
                  rootProps={{ isInvalid: !!errors?.closing_date, errorMessage: errors?.closing_date?.message }}
                  {...register('closing_date')}
                />
              ) : null}

              <Switch
                isChecked={!!formValues.disable_comments}
                onChange={event => setValue('disable_comments', event.target.checked)}
                fontWeight='normal'
                display='flex'
                alignItems='center'
              >
                {t('pages.application_predictions.components.predictions_create_modal.predictions_create_disable_comments')}
              </Switch>

              <Switch
                isChecked={!!formValues.enable_home}
                onChange={event => setValue('enable_home', event.target.checked)}
                fontWeight='normal'
                display='flex'
                alignItems='center'
              >
                {t('pages.application_predictions.components.predictions_create_modal.predictions_create_enable_home')}
              </Switch>                  
            </Flex>
          </ModalBody>

          <ModalFooter gap='kilo'>
            <Flex
              w='100%'
              direction={{ base: 'column', md: 'row' }}
              justifyContent={{ md: 'space-between' }}
              alignItems={{ md: 'center' }}
              gap={2}
            >
              <Button
                display='flex'
                alignItems='center'
                gap={2}
                onClick={() => {
                  setValue('status', PredictionsStatus.DRAFT);
                  formRef.current?.requestSubmit();
                }}
                width={{ base: '100%' }}
                p={0}
                color='brand.500'
                backgroundColor='transparent'
                textDecoration='underline'
                _hover={{
                  backgroundColor: 'transparent',
                  color: 'brand.500',
                }}
              >
                <ChakraFeatherIcon name='save' />
                {t('pages.application_predictions.components.predictions_create_modal.predictions_create_save_as_draft')}
              </Button>

              <Flex direction={{ base: 'column', md: 'row' }} w='100%' justifyContent='end' gap={4}>
                <Button variant='outlined' color='gray.600' onClick={handleClose}>
                  {t('pages.application_predictions.components.predictions_create_modal.predictions_create_cancel')}
                </Button>
                <Button type='submit' bg='green.500' paddingX={10}>
                  {t('pages.application_predictions.components.predictions_create_modal.predictions_create_create')}
                </Button>
              </Flex>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <CenteredSpinner isLoading={postMutation.isLoading} />
    </>
  );
};
